/**
 * EnterpriseTalent — Round 77 (A-C-08)
 *
 * Adds an In-Scope / Discover mode toggle on top of the R73 directory.
 *   - In-Scope:   Floo hierarchy talent (existing R73 behavior).
 *   - Discover:   Connect-discoverable talent (existing system, untouched).
 *
 * UI/orchestration bridge only — backend RPCs remain separate.
 */
import { useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Loader2, Users, Building2, ShieldAlert, MapPin, Compass } from 'lucide-react';
import { useEnterpriseScope } from '@/hooks/useEnterpriseScope';
import { useFlooActiveScopeContext } from '@/hooks/useFlooActiveScopeContext';
import { ActiveScopeBadge } from '@/components/floo-network/ActiveScopeBadge';
import { BackToOptiNetworkButton } from '@/components/floo-network/BackToOptiNetworkButton';
import { type InScopeScopeType } from '@/hooks/useInScopeTalent';
import { useUnifiedTalentSearch, type UnifiedMode } from '@/hooks/useUnifiedTalentSearch';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';

const ROLE_LABELS: Record<string, string> = {
  MANAGER: 'Manager',
  OWNER: 'Owner',
  ORG_ADMIN: 'Org Admin',
  TECHNICIAN: 'Technician',
  ADVISOR: 'Service Advisor',
  STUDENT: 'Student',
  technician: 'Technician',
  advisor: 'Advisor',
  manager: 'Manager',
  student: 'Student',
};

const STATUS_LABELS: Record<string, string> = {
  seeking: 'Open to Work',
  quietly_open: 'Quietly Open',
};

export default function EnterpriseTalent() {
  const navigate = useNavigate();
  const { canViewInScopeTalent, scopeLabel } = useEnterpriseScope();
  // Phase 8 — use the Phase 6 active scope when the user has chosen
  // one; otherwise fall back to membership-derived scope so existing
  // behavior is preserved for users who never touch the switcher.
  const activeCtx = useFlooActiveScopeContext();
  const scopeType = activeCtx.scopeType;
  const scopeId = activeCtx.scopeId;

  const [mode, setMode] = useState<UnifiedMode>('in_scope');
  const [search, setSearch] = useState('');
  const [shopFilter, setShopFilter] = useState<string>('all');
  const [roleFilter, setRoleFilter] = useState<string>('all');

  const { results, isLoading, error, needsActiveShop, rawInScope } = useUnifiedTalentSearch({
    mode,
    scopeType: (scopeType as InScopeScopeType) ?? null,
    scopeId: scopeId ?? null,
    filters: {
      shopId: mode === 'in_scope' ? shopFilter : undefined,
      role: roleFilter === 'all' ? undefined : roleFilter,
      search,
    },
  });

  // Filter option lists (built from in-scope data; Connect mode hides the shop filter)
  const shops = useMemo(() => {
    const set = new Map<string, string>();
    rawInScope.forEach((r) => set.set(r.shop_id, r.shop_name));
    return Array.from(set, ([id, name]) => ({ id, name })).sort((a, b) => a.name.localeCompare(b.name));
  }, [rawInScope]);

  const roles = useMemo(() => {
    const s = new Set<string>();
    if (mode === 'in_scope') {
      rawInScope.forEach((r) => s.add(r.role));
    } else {
      results.forEach((r) => r.role && s.add(r.role));
    }
    return Array.from(s).sort();
  }, [mode, rawInScope, results]);

  if (!canViewInScopeTalent) {
    return (
      <div className="container mx-auto p-6 max-w-2xl">
        <Card>
          <CardHeader>
            <div className="flex items-center gap-2">
              <ShieldAlert className="h-5 w-5 text-muted-foreground" />
              <CardTitle>Access Restricted</CardTitle>
            </div>
            <CardDescription>
              In-scope talent visibility is available to managers, owners, and org admins only.
            </CardDescription>
          </CardHeader>
        </Card>
      </div>
    );
  }

  const handleRowClick = (r: typeof results[number]) => {
    if (r.source === 'in_scope') {
      navigate(`/enterprise/talent/${r.user_id}?mode=in_scope`);
    } else if (r.technician_profile_id) {
      navigate(`/technicians/${r.technician_profile_id}`);
    } else {
      navigate(`/connect/talent/${r.user_id}`);
    }
  };

  return (
    <div className="container mx-auto p-6 max-w-6xl space-y-4">
      <BackToOptiNetworkButton testId="enterprise-talent-back-to-opti-network" />
      <div>
        <div className="flex items-center gap-2">
          <Building2 className="h-5 w-5 text-primary" />
          <h1 className="text-2xl font-semibold">{scopeLabel} Talent</h1>
        </div>
        <p className="text-sm text-muted-foreground mt-1">
          Search talent within your organization or discover open candidates through Connect.
        </p>
        {/* Phase 8 — active scope indicator. Hidden when no scope resolves. */}
        <ActiveScopeBadge className="mt-2" testId="floo-active-scope-badge-talent" />
      </div>

      <Tabs value="directory" className="w-full">
        <TabsList>
          <TabsTrigger value="directory">Directory</TabsTrigger>
          <TabsTrigger value="analytics" onClick={() => navigate('/enterprise/talent/analytics')}>
            Analytics
          </TabsTrigger>
          <TabsTrigger value="training" onClick={() => navigate('/enterprise/training')}>
            Training
          </TabsTrigger>
          <TabsTrigger value="suggestions" onClick={() => navigate('/enterprise/talent/suggestions')}>
            Suggestions
          </TabsTrigger>
        </TabsList>
      </Tabs>

      {/* Mode toggle — UI/orchestration bridge between in-scope and Connect */}
      <Tabs value={mode} onValueChange={(v) => setMode(v as UnifiedMode)} className="w-full">
        <TabsList>
          <TabsTrigger value="in_scope" className="gap-1.5">
            <Building2 className="h-3.5 w-3.5" />
            In-Scope Talent
          </TabsTrigger>
          <TabsTrigger value="discover" className="gap-1.5">
            <Compass className="h-3.5 w-3.5" />
            Discover Talent
          </TabsTrigger>
        </TabsList>
      </Tabs>

      <Card>
        <CardHeader>
          <div className="flex flex-col sm:flex-row gap-3">
            <Input
              placeholder={mode === 'in_scope' ? 'Search name or shop…' : 'Search name…'}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="sm:max-w-xs"
            />
            {mode === 'in_scope' && (
              <Select value={shopFilter} onValueChange={setShopFilter}>
                <SelectTrigger className="sm:w-[200px]">
                  <SelectValue placeholder="All shops" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All shops</SelectItem>
                  {shops.map((s) => (
                    <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
            <Select value={roleFilter} onValueChange={setRoleFilter}>
              <SelectTrigger className="sm:w-[180px]">
                <SelectValue placeholder="All roles" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All roles</SelectItem>
                {roles.map((r) => (
                  <SelectItem key={r} value={r}>{ROLE_LABELS[r] ?? r}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </CardHeader>
        <CardContent>
          {needsActiveShop ? (
            <div className="flex flex-col items-center justify-center py-12 text-muted-foreground">
              <Compass className="h-8 w-8 mb-2" />
              <p className="text-sm">Select an active shop to discover Connect talent.</p>
            </div>
          ) : isLoading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
            </div>
          ) : error ? (
            <p className="text-sm text-destructive py-6 text-center">
              {error.message}
            </p>
          ) : results.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12 text-muted-foreground">
              <Users className="h-8 w-8 mb-2" />
              <p className="text-sm">
                {mode === 'in_scope'
                  ? 'No matching talent in your scope.'
                  : 'No discoverable Connect talent matches these filters.'}
              </p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead>{mode === 'in_scope' ? 'Shop' : 'Status'}</TableHead>
                  <TableHead>Role</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead className="text-right">Source</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {results.map((r) => (
                  <TableRow key={`${r.source}-${r.user_id}-${r.shop_id ?? 'connect'}`}>
                    <TableCell>
                      {r.source === 'in_scope' ? (
                        <Link
                          to={`/enterprise/talent/${r.user_id}?mode=in_scope`}
                          className="text-primary font-medium hover:underline"
                        >
                          {r.display_name}
                        </Link>
                      ) : (
                        <button
                          type="button"
                          onClick={() => handleRowClick(r)}
                          className="text-primary font-medium hover:underline text-left"
                        >
                          {r.display_name}
                        </button>
                      )}
                    </TableCell>
                    <TableCell className="text-sm">
                      {r.source === 'in_scope' ? (
                        r.shop_name
                      ) : (
                        <div className="flex items-center gap-2">
                          {r.open_to_work_status && STATUS_LABELS[r.open_to_work_status] && (
                            <Badge
                              variant={r.open_to_work_status === 'seeking' ? 'default' : 'secondary'}
                              className="text-xs"
                            >
                              {STATUS_LABELS[r.open_to_work_status]}
                            </Badge>
                          )}
                          {r.distance_miles != null && (
                            <span className="text-xs text-muted-foreground flex items-center gap-0.5">
                              <MapPin className="h-3 w-3" />
                              ~{r.distance_miles} mi
                            </span>
                          )}
                        </div>
                      )}
                    </TableCell>
                    <TableCell>
                      {r.role && (
                        <Badge variant="outline" className="text-xs">
                          {ROLE_LABELS[r.role] ?? r.role}
                        </Badge>
                      )}
                    </TableCell>
                    <TableCell className="text-sm text-muted-foreground">
                      {r.technician_type_title ?? '—'}
                    </TableCell>
                    <TableCell className="text-right">
                      {r.source === 'in_scope' ? (
                        <Badge
                          variant="outline"
                          className="text-xs border-success/40 text-success-foreground bg-success/10"
                        >
                          In Your {r.scope_label ?? scopeLabel}
                        </Badge>
                      ) : (
                        <Badge variant="outline" className="text-xs">
                          Connect
                        </Badge>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
